import { h } from 'preact';
import { useState, useRef, useEffect, useMemo } from 'preact/hooks';
import Panel from 'utils/panel';
import TrapFocus from 'utils/trap-focus';
import { formatMoney, formatCartLinesAjax } from 'utils/storefront/format-resource';
import CloseIcon from 'utils/icon/icon-close.svg';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import FreeShipping from './shipping-bar';
import CartRecommendations from './cart-recommendations';
import DonationsProduct from './donations-product';
import CartGwpThreshold from './cart-gwp-threshold';

const Cart = (props) => {
  const { cart } = props;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const [isOpen, setIsOpen] = useState(isCartRedirect);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const hasDonationProduct = useMemo(
    () => cart.state.items?.some((item) => item.product_type === 'Donation'),
    [cart.state],
  );

  let trapFocus = null;

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-minicart]'));
      document.body.classList.add('scroll-lock', 'has-minicart');
      trapFocus.init();

      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if(kustomerIframe && !kustomerIframe.classList.contains("kustomer-chat-window-left")) {
        kustomerIframe.classList.add("kustomer-chat-window-left");
      }
    } else {
      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if(kustomerIframe && kustomerIframe.classList.contains("kustomer-chat-window-left")) {
        kustomerIframe.classList.remove("kustomer-chat-window-left");
      }
    }

    return () => {
      document.body.classList.remove('scroll-lock', 'has-minicart');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-header-cart-trigger]'));
    };
  }, [isOpen, trapFocus]);

  const cartRecommendations = useMemo(
    () => (
      <CartRecommendations
        title={props.data.recTitle}
        placementId={props.data.placementId}
        layout={props.data.layout}
      />
    ),
    [props.data.recTitle, props.data.placementId, props.data.layout],
  );

  const EmptyCartContent = () => (
    <div>
      <div className="flex flex-col items-center gap-15 py-40 lg:py-[50px]">
        <h4 className="text-faded-black">{window.TMW.Strings.cart.empty}</h4>
        {props.data.emptyButtonLink && props.data.emptyButtonText && (
          <a
            className="cta-blue no-underline"
            href={props.data.emptyButtonLink}
          >
            {props.data.emptyButtonText}
          </a>
        )}
      </div>

      {props.data.placementId && <div className="border-t border-grey-5">{cartRecommendations}</div>}
    </div>
  );

  const activeGwpDiscounts = useMemo(() => {
    const now = new Date();
    return props.data.gwp.filter((gwp) => new Date(gwp.startsAt) < now && (!gwp.endsAt || new Date(gwp.endsAt) > now));
  }, []);

  const lines = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);
  const showShippingBar =
    cart.state.item_count > 0 && props.data.freeShippingThreshold && props.data.freeShippingThreshold !== 0;

  const getCheckoutUrl = () => {
    const storedPromo = JSON.parse(localStorage.getItem('promo'));
    if (storedPromo) {
      return `/discount/${storedPromo.discount_code}?redirect=/checkout`;
    }
    return '/checkout';
  };

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="cart"
      showBackdrop={true}
      additionalClasses="w-full max-w-[420px] cart-panel"
    >
      <div
        className="relative flex h-full flex-col"
        data-minicart
      >
        <button
          className="absolute right-15 top-20 text-faded-black lg:right-20"
          ref={closeTriggerEl}
          aria-label={window.TMW.Strings.cart.close}
        >
          <CloseIcon />
        </button>
        <h4 className="border-b border-grey-5 px-15 py-20 text-faded-black lg:px-20">
          {window.TMW.Strings.cart.title}
          {cart.state.item_count > 0 && <span className="inline-block pl-4">({cart.state.item_count})</span>}
        </h4>
        {props.data.placementMessageId && props.data.useDomaineMarkup && (
          <div>
            <div
              className="nosto_element"
              id={props.data.placementMessageId}
            ></div>
          </div>
        )}
        {!props.data.useDomaineMarkup && (
          <div id="custom_nosto_element">
            <div className="nosto_element"></div>
          </div>
        )}
        {!!showShippingBar && (
          <FreeShipping
            threshold={props.data.freeShippingThreshold}
            cart={cart}
          />
        )}
        {cart.state.item_count > 0 && !!activeGwpDiscounts.length && (
          <div className="flex flex-col gap-20 p-20">
            {activeGwpDiscounts.map((gwp) => (
              <CartGwpThreshold
                key={gwp.id}
                gwp={gwp}
              />
            ))}
          </div>
        )}
        {cart.state.item_count === 0 ? (
          <EmptyCartContent />
        ) : (
          <div className="flex h-auto grow flex-col overflow-y-auto border-t border-grey-5">
            {lines.map((line, index) => (
              <CartItem
                key={line.id}
                line={line}
                index={index}
              />
            ))}
            {!hasDonationProduct && props.data.donationProduct?.variants?.length > 0 && (
              <DonationsProduct donationProduct={props.data.donationProduct} />
            )}
            {props.data.placementId && <div id={props.data.placementId}></div>}
          </div>
        )}
        {cart.state.item_count > 0 && (
          <div className="border-t border-grey-5 px-15 py-20 lg:px-20">
            <div className="mb-12 flex items-center justify-between">
              <h4 className="body-14">{window.TMW.Strings.cart.merchandise}</h4>
              <span className="body-14">
                <span className="body-14">
                  {cart.mode === 'storefront'
                    ? formatMoney(
                        cart.state.cart_level_discount_applications?.length
                          ? { amount: cart.state.original_total_price }
                          : cart?.state?.subtotal || { amount: 0 },
                      )
                    : formatMoney(
                        cart.state.cart_level_discount_applications?.length
                          ? { amount: cart.state.original_total_price }
                          : cart?.state?.total_price
                            ? { amount: cart.state.total_price }
                            : { amount: 0 },
                        true,
                      )}
                </span>
              </span>
            </div>
            <div className="mb-12 flex items-center justify-between">
              <h4
                className="body-14"
                dangerouslySetInnerHTML={{ __html: window.TMW.Strings.cart.shippingAndTaxes }}
              ></h4>
              <span className="body-14">{window.TMW.Strings.cart.calculatedAtCheckout}</span>
            </div>
            <div className="mb-12 flex items-center justify-between">
              <h4 className="body-14">
                {cart.state.cart_level_discount_applications?.length > 0
                  ? window.TMW.Strings.cart.subtotalWithDiscount
                  : window.TMW.Strings.cart.subtotal}
              </h4>
              <span className="">
                {cart.mode === 'storefront'
                  ? formatMoney(cart?.state?.subtotal || { amount: 0 })
                  : formatMoney(cart?.state?.total_price ? { amount: cart.state.total_price } : { amount: 0 }, true)}
              </span>
            </div>
            <a
              id="checkout"
              className="cta-blue h-45 w-full justify-center rounded py-15 no-underline"
              href={getCheckoutUrl()}
            >
              {cart.mode === 'storefront' ? cart?.state?.checkoutUrl : window.TMW.Strings.cart.checkout}
            </a>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
